import React from "react"
import "../styles.css"
import axios from "axios"
import { toast } from "react-toastify"

const defaultSummaryData = [
  { title: "Total Deposits", value: "$0", icon: "💰" },
  { title: "Total BTC Holding", value: "0 BTC", icon: "₿" },
  { title: "Average Price", value: "$0", icon: "🏷️" },
  { title: "Current Value", value: "$0", icon: "📈" },
]

const StatTileSkeleton = () => (
  <div className="stat-tile sk-panel">
    <div className="skeleton sk-line w50" />
    <div className="skeleton sk-line w70 sk-lg" />
  </div>
)

const TableSkeleton = () => (
  <div className="transaction-table-skeleton">
    <div className="row-skeleton skeleton" style={{ marginBottom: "24px" }} />
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="row-skeleton skeleton" />
    ))}
  </div>
)

// Helper function to parse currency strings (handles $, commas, etc.)
const parseCurrency = (value) => {
  if (!value) return 0
  if (typeof value === 'number') return value
  // Remove currency symbols, commas, and whitespace, then parse
  const cleaned = String(value).replace(/[$₦,\s]/g, '')
  return parseFloat(cleaned) || 0
}

const formatCurrency = (value) => {
  const numValue = typeof value === 'string' ? parseCurrency(value) : (value || 0)
  return `$${Number(numValue).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// const formatNGN = (value) => {
//   const numValue = typeof value === 'string' ? parseCurrency(value) : (value || 0)
//   return `₦${Number(numValue).toLocaleString(undefined, {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   })}`
// }
const formatBTC = (value) => {
  if (!value || value === "") return "0 BTC"
  const numValue = typeof value === 'string' ? parseFloat(value) : (value || 0)
  return `${Number(numValue)?.toFixed(8)} BTC`
}

const transformSummaryData = (data) => {
  const summaryEntry = data.find((entry) => entry.Month === "Sum/Average" || entry.month === "Sum/Average")
  if (!summaryEntry) return defaultSummaryData

  return [
    {
      title: "Total Deposits",
      icon: "💰",
      value: formatCurrency(summaryEntry["Total Deposits"]),
    },
    {
      title: "Total BTC Holding",
      icon: "₿",
      value: formatBTC(summaryEntry["BTC Purchased"]),
    },
    {
      title: "Average Price",
      icon: "🏷️",
      value: formatCurrency(summaryEntry["Price"]),
    },
    {
      title: "Current Value",
      icon: "📈",
      value: formatCurrency(summaryEntry["Current Value"]),
    },
  ]
}

const SummaryCards = ({ data }) => (
  <div className="stat-tiles report-tiles">
    {data.map((item, i) => (
      <div
        className={`stat-tile ${i === data.length - 1 ? "tile-accent" : ""}`}
        key={i}
      >
        <div className="stat-tile-label">{item.title}</div>
        <div className="stat-tile-value">{item.value}</div>
      </div>
    ))}
  </div>
)

const ReportTable = ({ rows }) => {
  const [sortOrder, setSortOrder] = React.useState("desc") // 'asc' or 'desc'

  const sortedRows = React.useMemo(() => {
    if (!rows?.length) return []

    const monthOrder = {
      January: 0,
      February: 1,
      March: 2,
      April: 3,
      May: 4,
      June: 5,
      July: 6,
      August: 7,
      September: 8,
      October: 9,
      November: 10,
      December: 11,
    }

    return [...rows].sort((a, b) => {
      // Handle summary row (though it should be filtered out already)
      if (a.Month === "Sum/Average" || a.month === "Sum/Average") return 1
      if (b.Month === "Sum/Average" || b.month === "Sum/Average") return -1

      const yearA = parseInt(a.Year || a.year) || 2025
      const yearB = parseInt(b.Year || b.year) || 2025
      const monthA = monthOrder[a.month || a.Month] ?? -1
      const monthB = monthOrder[b.month || b.Month] ?? -1

      // Compare years first
      if (yearA !== yearB) {
        return sortOrder === "asc" ? yearA - yearB : yearB - yearA
      }

      // If same year, compare months
      return sortOrder === "asc" ? monthA - monthB : monthB - monthA
    })
  }, [rows, sortOrder]).filter((row) => row["Wallet Address"] != null)

  const toggleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
  }

  if (!rows?.length) {
    return null
  }

  return (
    <div className="transaction-table-container">
      <table className="transaction-table">
        <thead>
          <tr>
            <th onClick={toggleSort} style={{ cursor: "pointer" }}>
              Month{" "}
              <span
                className={`sort-indicator ${
                  sortOrder === "asc" ? "asc" : "desc"
                }`}
              >
                &gt;
              </span>
            </th>
            <th>Deposits</th>
            <th style={{ minWidth: "105px" }}>Value in BTC</th>
            <th>Price</th>
            <th>Total Deposit</th>
            <th>NGN Rate</th>
            <th>Wallet Address</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr key={i}>
              <td>
                {row.month || row.Month}, {row.year || row.Year || "2025"}
              </td>
              <td>{row["Total Deposits"]}</td>
              <td>{formatBTC(row["BTC Purchased"])}</td>
              <td>{row["Price"]}</td>
              <td>{row["Current Value"]}</td>
              <td>{row["NGN Rate"]}</td>
              <td
                style={{
                  fontSize: "0.97em",
                  wordBreak: "break-all",
                  cursor: "pointer",
                  opacity: 0.85,
                }}
                onClick={() => {
                  navigator.clipboard.writeText(row["Wallet Address"])
                  toast.success("Copied Wallet Address to clipboard")
                }}
              >
                {row["Wallet Address"]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const ErrorState = ({ error, onRetry }) => (
  <div
    style={{
      textAlign: "center",
      padding: "32px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "16px",
    }}
  >
    <div style={{ color: "#ffffff", marginBottom: "8px" }}>{error}</div>
    <button
      onClick={onRetry}
      style={{
        background: "none",
        border: "2px solid #23272f",
        borderRadius: "8px",
        padding: "8px 16px",
        color: "#fff",
        cursor: "pointer",
        transition: "all 0.2s",
      }}
      onMouseOver={(e) =>
        (e.target.style.background = "rgba(255, 255, 255, 0.1)")
      }
      onMouseOut={(e) => (e.target.style.background = "none")}
    >
      Refetch Data
    </button>
  </div>
)

const CommunityReportScreen = () => {
  const [reportsData, setReportsData] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState(null)

  const fetchReportsData = React.useCallback(async () => {
    try {
      setError(null)
      setIsLoading(true)
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/reports`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_key")}`,
          },
        }
      )
      setReportsData(response.data)
    } catch (error) {
      console.error("Error fetching reports data:", error)
      setError("Failed to load reports data")
    } finally {
      setIsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchReportsData()
  }, [fetchReportsData])

  const summaryData = React.useMemo(() => {
    if (!reportsData) return defaultSummaryData
    return transformSummaryData(reportsData)
  }, [reportsData])

  const tableData = React.useMemo(() => {
    if (!reportsData) return []
    return reportsData.filter((entry) => entry.Month !== "Sum/Average")
  }, [reportsData])

  return (
    <div className="dashboard-content">
      <h1 className="dashboard-title">Reports</h1>
      <div className="dashboard-subtitle" style={{ marginBottom: 24 }}>
        Monthly community contributions, BTC purchased and current valuation.
      </div>

      {isLoading ? (
        <>
          <div className="stat-tiles report-tiles">
            {[1, 2, 3, 4].map((i) => (
              <StatTileSkeleton key={i} />
            ))}
          </div>
          <TableSkeleton />
        </>
      ) : error ? (
        <ErrorState error={error} onRetry={fetchReportsData} />
      ) : (
        <>
          <SummaryCards data={summaryData} />
          <ReportTable rows={tableData} />
        </>
      )}
    </div>
  )
}

export default CommunityReportScreen
